import {JsonPlatformRestaurantAvailability} from '@deliveryhero/vendor-growth-api-client';
import {useLogger, useSharedDispatch, useSharedState} from '@deliveryhero/vendor-web-core';
import {useEffect, useCallback, useRef, MutableRefObject} from 'react';
import {AVAILABILITY_POLLING_PERIOD_MS} from '../../constants/Intervals';
import {FetchAvailabilities, availabilityApiDict, useApi} from '../../hooks/useApi';
import {useHighDemandFlag} from '../../hooks/useHighDemandFlag';
import {AUTH_STATE} from '../../interfaces/AuthState';
import {isAvailabilityEnabledSelector} from '../../selectors/isAvailabilityEnabledSelector';
import {
  AVAILABILITIES_RECEIVED,
  AVAILABILITY_STATE,
  AvailabilityStore,
} from '../../slices/AvailabilityStateSlice';

const restartPollingTimestampSelector = (store: AvailabilityStore): number | undefined => {
  return store[AVAILABILITY_STATE]?.restartPollingTimestamp;
};

export function AvailabilityStateDaemon(): JSX.Element | null {
  useHighDemandFlag();

  const dispatch = useSharedDispatch();
  const isSliceAvailable = useSharedState((state: AvailabilityStore): boolean => {
    return state[AVAILABILITY_STATE] !== undefined;
  });
  const restartPollingTimestamp = useSharedState(restartPollingTimestampSelector);
  const isAuthenticated = useSharedState(
    (state) =>
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      (state[AUTH_STATE] as {accessToken: string | undefined})?.accessToken !== undefined,
  );
  const isVendorAvailabilityEnabled = useSharedState(isAvailabilityEnabledSelector);
  const logger = useLogger();
  const poller: MutableRefObject<NodeJS.Timeout | undefined> = useRef(undefined);
  const {fetchData: fetchAvailabilities} = useApi<FetchAvailabilities>(
    availabilityApiDict.fetchAvailabilities,
  );

  const fetchAvailabilitiesAndDispatch = useCallback(async () => {
    const availabilities: JsonPlatformRestaurantAvailability[] | undefined =
      await fetchAvailabilities();
    if (!Array.isArray(availabilities)) {
      logger.error(
        'AvailabilityStateDaemon.getAvailabilityState: Empty body or invalid response received',
        {},
        {availabilities},
      );
      return;
    }
    if (!availabilities.length) {
      logger.warning(
        'AvailabilityStateDaemon.getAvailabilityState: none of the platforms supports availability feature, stop polling.',
        {},
        {availabilities},
      );
      clearInterval(poller.current);
    }
    dispatch({
      type: AVAILABILITIES_RECEIVED,
      payload: {availabilities},
    });
    // TODO: useLogger returns new value on every rerender, that's why it's removed from depth for now
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, fetchAvailabilities]);

  useEffect(() => {
    const isPollingEnabled = isSliceAvailable && isAuthenticated && isVendorAvailabilityEnabled;

    if (isPollingEnabled) {
      if (restartPollingTimestamp === undefined) {
        void fetchAvailabilitiesAndDispatch();
      }
      poller.current = setInterval(
        () => void fetchAvailabilitiesAndDispatch(),
        AVAILABILITY_POLLING_PERIOD_MS,
      );
      return () => {
        clearInterval(poller.current);
      };
    } else {
      clearInterval(poller.current);
    }
  }, [
    isSliceAvailable,
    isAuthenticated,
    isVendorAvailabilityEnabled,
    restartPollingTimestamp,
    fetchAvailabilitiesAndDispatch,
  ]);

  return null;
}
